import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import type { SessionSummary } from '@/types/emotion';

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '--';

  const p = dateString.split(/[-T :.Z]/).filter(Boolean).map(Number);
  const utcDate = new Date(Date.UTC(p[0], p[1] - 1, p[2], p[3] || 0, p[4] || 0, p[5] || 0));

  // SHIFT: -14 hours FOR PAKISTAN TIME
  const shifted = new Date(utcDate.getTime() - (19 * 60 * 60 * 1000));

  const year = shifted.getUTCFullYear();
  const month = shifted.getUTCMonth();
  const day = shifted.getUTCDate();
  const hour = shifted.getUTCHours();
  const minute = shifted.getUTCMinutes();

  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  const displayMin = minute.toString().padStart(2, '0');
  
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthStr = monthNames[month];

  return `${monthStr} ${day}, ${year}, ${displayHour}:${displayMin} ${ampm}`;
};

const EngagementBadge = ({ score }: { score: number }) => {
  const getColor = () => {
    if (score >= 70) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (score >= 50) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-neon-red/20 text-neon-red border-neon-red/30';
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getColor()}`}>
      {score}%
    </span>
  );
};

export const SessionHistory = () => {
  const { getToken } = useAuth();

  const { data: sessions, isLoading, error } = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const token = await getToken();
      const response = await apiClient.get<SessionSummary[]>(
        '/sessions',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-background-border bg-background-surface p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dimmed">Loading sessions...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-neon-red/40 bg-neon-red/10 p-6 text-neon-red">
        Unable to load session history. Please try again later.
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="rounded-xl border border-background-border bg-background-surface p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-lg font-semibold text-text-primary mb-2">No sessions found</p>
          <p className="text-sm text-text-dimmed">Your past lecture sessions will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-background-border bg-background-surface p-6">
        <h1 className="text-2xl font-semibold text-text-primary mb-2">Session History</h1>
        <p className="text-sm text-text-dimmed">View and access reports for past lectures</p>
      </div>

      <div className="rounded-xl border border-background-border bg-background-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background-main/50 border-b border-background-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-primary uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-primary uppercase tracking-wider">Class Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-primary uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-text-primary uppercase tracking-wider">Engagement Score</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-text-primary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-border">
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-background-main/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {formatDate(session.startedAt)}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-text-primary">{session.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-dimmed">
                    {formatDuration(session.durationSeconds)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EngagementBadge score={session.averageEngagement} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <Link
                      to={`/sessions/${session.id}`}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-background-border text-text-primary hover:bg-brand/20 hover:border-brand transition-colors font-medium border border-background-border"
                    >
                      View Report
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};